import { Box, Button } from "@mui/material";
import Alert from "@mui/material/Alert";
import { Stack } from "@mui/system";
import { useMuiController } from "@sinch/components";
import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { SMSMessageInput } from "@sinch/components/form/SMSMessageInput/SMSMessageInput";
import { ToolbarButton } from "@sinch/components/form/TextEditor/components";
import { useFormat } from "@sinch/core/hooks/format";
import { isMobile } from "@sinch/utils/browser";
import { htmlToText } from "@sinch/utils/dom";
import { t } from "i18next";
import { pluck } from "ramda";
import { isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import React, { FC } from "react";
import { useWatch } from "react-hook-form";
import { MessageFormFields } from "./formSchema";
import { AvailableTemplateBox } from "./AvailableTemplateBox";
import type { Placeholders } from "./MessageForm";
import { useRecipients } from "./useRecipients";
import { useCustomPlaceholderInsert } from "./useReconfirmationDialog";

/**
 * SMSContent component renders a text input for SMS messages along with
 * character count information, segment count and alerts about message encoding.
 */
export const SMSContent: FC<{
  noNativeSend: boolean;
  placeholders: Placeholders;
  allowed?: boolean;
  fillDefaultTemplate?: boolean;
}> = ({ noNativeSend, placeholders, allowed, fillDefaultTemplate }) => {
  const { phone } = useFormat();
  const { field } = useMuiController<MessageFormFields, "textMessage">({
    name: "textMessage",
    disabled: !allowed,
  });

  const recipients = useWatch<MessageFormFields, "recipients">({
    name: "recipients",
    defaultValue: [],
  });

  const { recipientsData } = useRecipients({
    value: recipients,
    channels: [NotificationChannel.Sms],
    channelAddressProp: "phone",
  });
  const phoneNumbers = pluck("phone", recipientsData).filter(isNotNil).map(phone.plain!);

  const { ref, placeholderAddProps } = useCustomPlaceholderInsert(placeholders ?? []);

  return (
    <>
      {allowed && (
        <AvailableTemplateBox
          channel={NotificationChannel.Sms}
          fillDefaultTemplate={fillDefaultTemplate}
          onApplyTemplate={({ content }) => {
            field.onChange(content);
          }}
        />
      )}
      <Box sx={{ opacity: allowed ? 1 : 0.35 }}>
        <SMSMessageInput
          {...field}
          editorRef={ref}
          placeholders={placeholders.length > 1 ? placeholders : undefined}
          recipientCount={recipients.length}
          secondaryToolbar={
            isNotNilOrEmpty(placeholderAddProps) && placeholderAddProps.length === 1 ? (
              <>
                {placeholderAddProps.map((buttonProps, i) => (
                  <ToolbarButton key={i} {...buttonProps} disabled={field.disabled} />
                ))}
              </>
            ) : undefined
          }
        />
      </Box>
      {isMobile().isMobile && !noNativeSend && <NativeSMSSendNotice phoneNumbers={phoneNumbers} value={field.value} />}
    </>
  );
};

/**
 * NativeSMSSendNotice component displays a notice about opening the message in a native SMS app
 * with a button to do so.
 */
const NativeSMSSendNotice: FC<{ value: string; phoneNumbers: string[] }> = ({ value, phoneNumbers }) => {
  const phoneNumbersList = phoneNumbers.join(",");
  const encodedBody = encodeURIComponent(htmlToText(value) ?? "");
  const smsUri = isMobile().apple
    ? `sms:/open?addresses=${phoneNumbersList}&body=${encodedBody}`
    : `sms:${phoneNumbersList}?&body=${encodedBody}`;
  return (
    <Alert severity="info" variant="outlined">
      <Stack spacing={1}>
        <span>{t("Messages.openInSMSAppInfo")}</span>
        <Box>
          <Button color="info" fullWidth={false} href={smsUri} sx={{ whiteSpace: "nowrap" }} variant="contained">
            {t("Messages.openInSMSApp")}
          </Button>
        </Box>
      </Stack>
    </Alert>
  );
};
